import { useMemo, Fragment } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, Minus, ArrowRight, Table2 } from "lucide-react";
import { TAS_FLOW_FEATURES, TAS_FLOW_MODULES, TIER_ORDER } from "../data/tasFlowFeatures";
import { MODULE_EN, FEATURE_EN } from "../i18n/tasflowFeaturesEn";
import { useLang } from "../i18n/LanguageContext";
import { TASFLOW_I18N } from "../i18n/tasflow";

const TIERS = ["Starter", "Plus", "Pro"];

function ComparisonTable() {
  const { lang } = useLang();
  const t = TASFLOW_I18N[lang].features;
  const isEn = lang === "en";

  const grouped = useMemo(() => {
    const map = {};
    for (const f of TAS_FLOW_FEATURES) (map[f.module] ||= []).push(f);
    return TAS_FLOW_MODULES.filter((m) => map[m]).map((m) => [m, map[m]]);
  }, []);

  const moduleLabel = (m) => (isEn ? MODULE_EN[m] || m : m);
  const featureLabel = (n) => (isEn ? FEATURE_EN[n] || n : n);

  return (
    <section id="ozellikler" className="relative bg-[#020617] py-24 scroll-mt-24" data-testid="tasflow-features">
      <div className="absolute inset-0 bg-grid opacity-15" />
      <div className="container-x relative">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#F97316]/40 bg-[#F97316]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#FB923C]">
            <Table2 className="h-3 w-3" /> {t.badge}
          </span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-white">
            {t.title} <span className="bg-gradient-to-r from-[#F97316] to-[#FB923C] bg-clip-text text-transparent">{t.titleAccent}</span>
          </h2>
          <p className="mt-3 text-white/60">{t.desc}</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5 }}
          className="relative mt-10"
        >
          <p className="mb-3 flex items-center gap-1.5 text-xs text-white/40 sm:hidden" data-testid="tasflow-comparison-scroll-hint">
            <ArrowRight className="h-3 w-3" /> {t.scrollHint}
          </p>
          <div className="pointer-events-none absolute right-0 top-8 bottom-0 z-20 w-10 bg-gradient-to-l from-[#020617] to-transparent sm:hidden" />
          <div className="overflow-x-auto rounded-3xl border border-white/10 bg-white/[0.02]">
            <table className="w-full min-w-[860px] table-fixed border-collapse text-left" data-testid="tasflow-comparison-table">
              <thead>
                <tr className="sticky top-16 lg:top-20 z-30 border-b border-white/10 bg-[#020617]">
                  <th className="sticky left-0 z-10 bg-[#020617] px-5 py-5 text-xs font-bold uppercase tracking-[0.14em] text-white/45">
                    {t.colFeature}
                  </th>
                  {TIERS.map((tier) => (
                    <th
                      key={tier}
                      className={`w-40 px-4 py-5 text-center font-heading text-base font-extrabold ${
                        tier === "Plus" ? "text-[#FB923C]" : "text-white"
                      }`}
                    >
                      <span className="inline-flex flex-col items-center gap-1">
                        {tier}
                        {tier === "Plus" && (
                          <span className="rounded-full bg-gradient-to-r from-[#F97316] to-[#FB923C] px-2 py-0.5 text-[8px] font-bold uppercase tracking-wide text-white">
                            {t.popular}
                          </span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grouped.map(([module, feats]) => (
                  <Fragment key={module}>
                    <tr className="bg-white/[0.04]">
                      <td colSpan={4} className="sticky left-0 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#22D3EE]">
                        {moduleLabel(module)}
                      </td>
                    </tr>
                    {feats.map((row) => (
                      <tr key={row.name} className="border-b border-white/[0.06] last:border-0 hover:bg-white/[0.02] transition-colors">
                        <td className="sticky left-0 z-10 bg-[#020617] px-5 py-3.5 text-sm text-white/80 whitespace-nowrap">{featureLabel(row.name)}</td>
                        {TIERS.map((tier) => {
                          const included = TIER_ORDER[tier] >= TIER_ORDER[row.tier];
                          return (
                            <td key={tier} className={`px-4 py-3.5 text-center ${tier === "Plus" ? "bg-[#F97316]/[0.04]" : ""}`}>
                              {included ? (
                                <Check className="mx-auto h-4 w-4 text-[#FB923C]" strokeWidth={3} />
                              ) : (
                                <Minus className="mx-auto h-4 w-4 text-white/20" />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <p className="mt-6 text-xs text-white/40">{t.footnote(TAS_FLOW_FEATURES.length)}</p>

        <div className="mt-8">
          <Link
            to="/proje-talep"
            data-testid="tasflow-features-cta"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#F97316] to-[#FB923C] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-[#F97316]/25 transition hover:-translate-y-0.5"
          >
            {t.cta} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Packages() {
  const { lang } = useLang();
  const c = TASFLOW_I18N[lang];
  const t = c.packagesSection;

  return (
    <section id="paketler" className="py-24 scroll-mt-24" data-testid="tasflow-packages">
      <div className="container-x">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#F97316]/40 bg-[#F97316]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#FB923C]">
            {t.badge}
          </span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-white">{t.title}</h2>
          <p className="mt-3 text-white/60">{t.desc}</p>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-3 items-start">
          {c.packages.map((p) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
              data-testid={`tasflow-package-${p.id}`}
              className={`relative rounded-3xl p-8 backdrop-blur ${
                p.highlight
                  ? "border-2 border-[#F97316]/50 bg-gradient-to-b from-[#F97316]/10 to-white/[0.02] lg:-mt-4 lg:mb-4 shadow-2xl shadow-[#F97316]/10"
                  : "border border-white/10 bg-white/[0.03]"
              }`}
            >
              {p.badge && (
                <span className={`absolute -top-3 left-8 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${
                  p.highlight ? "bg-gradient-to-r from-[#F97316] to-[#FB923C] text-white" : "bg-[#22D3EE]/15 text-[#22D3EE] border border-[#22D3EE]/30"
                }`}>
                  {p.badge}
                </span>
              )}
              <h3 className="font-heading text-2xl font-extrabold text-white">{p.name}</h3>
              <p className="mt-2 text-sm text-white/55 leading-relaxed min-h-[40px]">{p.tagline}</p>

              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-heading text-3xl font-extrabold text-white">{p.priceLabel}</span>
              </div>
              <div className="mt-1 text-xs text-white/40">{p.priceNote}</div>

              <div className="mt-5 inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1.5 text-sm font-semibold text-white">
                <span className="bg-gradient-to-r from-[#F97316] to-[#FB923C] bg-clip-text text-transparent">{p.featureCount}</span>
                <span className="text-white/60">{t.featureWord}</span>
              </div>

              <ul className="mt-6 space-y-2.5">
                {p.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2.5 text-sm text-white/75">
                    <Check className="h-4 w-4 mt-0.5 shrink-0 text-[#FB923C]" /> {h}
                  </li>
                ))}
              </ul>

              <Link
                to="/proje-talep"
                data-testid={`tasflow-package-cta-${p.id}`}
                className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold transition ${
                  p.highlight
                    ? "bg-gradient-to-r from-[#F97316] to-[#FB923C] text-white shadow-lg shadow-[#F97316]/25 hover:-translate-y-0.5"
                    : "border border-white/15 text-white/85 hover:border-white/40 hover:text-white"
                }`}
              >
                {p.cta} <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          ))}
        </div>

        {/* pricing model */}
        <div className="mt-14 rounded-3xl border border-white/10 bg-white/[0.02] p-8">
          <h3 className="font-heading text-lg font-bold text-white">{t.pricingTitle}</h3>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {c.pricingModel.map((m, i) => (
              <div key={m.title} className="rounded-2xl border border-white/8 bg-white/[0.03] p-5">
                <div className="font-heading text-2xl font-extrabold text-[#F97316]/30">{i + 1}</div>
                <div className="mt-2 font-heading text-sm font-semibold text-white">{m.title}</div>
                <p className="mt-1.5 text-xs text-white/50 leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-white/50 leading-relaxed">{c.pricingNote}</p>
        </div>
      </div>
    </section>
  );
}

export default function TasFlowFeatures() {
  return (
    <>
      <Packages />
      <ComparisonTable />
    </>
  );
}
